import { useLazyQuery } from '@apollo/client'
import { GET_STORE, GetStoreQuery, GetStoreQueryVariables } from '@dai/graphql'
// import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Alert,
  Box,
  CircularProgress,
  Container as MuiContainer,
  CssBaseline,
  Paper as MuiPaper,
} from '@mui/material'
import { spacing } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import CenteredBox from '@/components/CenteredBox'
import Footer from '@/components/Footer'
import GlobalStyle from '@/components/GlobalStyle'
import Navbar from '@/components/navbar/Navbar'
import dashboardItems from '@/components/sidebar/dashboardItems'
import Sidebar from '@/components/sidebar/Sidebar'

const drawerWidth = 258
const collapsedDrawerWidth = 80

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`

const Drawer = styled.div<{ collapsed?: boolean }>`
  ${props => props.theme.breakpoints.up('md')} {
    width: ${props => (props.collapsed ? collapsedDrawerWidth : drawerWidth)}px;
    flex-shrink: 0;
    transition: width 0.2s ease-in-out;
  }
`

const AppContent = styled.div<{ collapsed?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 0;
  transition: max-width 0.2s ease-in-out;
  ${props => props.theme.breakpoints.up('md')} {
    max-width: calc(
      100% -
        ${props => (props.collapsed ? collapsedDrawerWidth : drawerWidth)}px
    );
  }
`

const Paper = styled(MuiPaper)(spacing)

const Container = styled(MuiContainer)`
  height: 100%;
`

const MainContent = styled(Paper)`
  flex: 1;
  background: ${props => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`
interface DashboardType {
  children?: React.ReactNode
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const router = useLocation()
  const { id } = useParams()
  // const {
  //   handle: { handleNavTo },
  // } = useUrlSearchParams()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false)
  }, [router.pathname])

  const getPageTitle = () => {
    const path = router.pathname
    if (path.includes('/reporting/user')) {
      return 'User Reporting'
    }
    if (path.includes('/reporting/plan')) {
      return 'Plan'
    }
    if (path.includes('/reporting/geographic')) {
      return 'Geographic Reporting'
    }
    return undefined
  }

  const pathname = window.location.pathname

  const [getStore, storeData] = useLazyQuery<
    GetStoreQuery,
    GetStoreQueryVariables
  >(GET_STORE)

  useEffect(() => {
    if (id && pathname.includes('stores')) {
      getStore({ variables: { storeId: id } })
    }
  }, [id, pathname, getStore])

  const Body = pathname.includes('stores') ? (
    storeData.loading ? (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    ) : storeData.error ? (
      <CenteredBox>
        <Alert severity="error">{storeData.error.message}</Alert>
      </CenteredBox>
    ) : (
      <Outlet />
    )
  ) : (
    <Outlet />
  )

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer collapsed={sidebarCollapsed}>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems()}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar
            PaperProps={{
              style: {
                width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              },
            }}
            items={dashboardItems()}
            onCollapse={handleSidebarCollapse}
            collapsed={sidebarCollapsed}
          />
        </Box>
      </Drawer>
      <AppContent collapsed={sidebarCollapsed}>
        <Navbar
          onDrawerToggle={handleDrawerToggle}
          pageTitle={getPageTitle()}
        />
        <MainContent>
          <Container maxWidth="xl" sx={{ pb: 2 }}>
            {children}
            {Body}
          </Container>
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  )
}

export default Dashboard
